import AsyncStorage from '@react-native-async-storage/async-storage';
import type { ChatPlannerResult } from '@/services/aiPlanner';

type CachedChatPlannerResult = {
  savedAt: number;
  result: ChatPlannerResult;
};

/** Stable key segment from chatPlanner `intentAppendix` (Plan focus). Must match between get/set. */
export function buildPlannerFocusCacheKey(appendix: string | null | undefined): string {
  const t = typeof appendix === 'string' ? appendix.trim() : '';
  if (!t) return 'default';
  let h = 5381;
  for (let i = 0; i < t.length; i++) {
    h = (((h << 5) + h) ^ t.charCodeAt(i)) >>> 0;
  }
  return `f${h.toString(36)}`;
}

const legacyKey = (tripId: string, mode: 'itinerary' | 'packages') =>
  `@tripflow_ai_suggestions_cache:${tripId}:${mode}`;

const v2Key = (tripId: string, mode: 'itinerary' | 'packages', focusKey: string) =>
  `@tripflow_ai_suggestions_cache_v2:${tripId}:${mode}:${focusKey}`;

function parseCached(raw: string | null): CachedChatPlannerResult | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CachedChatPlannerResult;
    if (!parsed?.result || typeof parsed.savedAt !== 'number') return null;
    if (!Array.isArray(parsed.result.suggestions)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * @param plannerFocusKey - From `buildPlannerFocusCacheKey(intentAppendix)`. Omit or `default` for packages / no focus.
 */
export async function setCachedChatPlannerResult(
  tripId: string,
  mode: 'itinerary' | 'packages',
  result: ChatPlannerResult,
  plannerFocusKey?: string,
): Promise<void> {
  try {
    const fk = mode === 'packages' ? 'default' : plannerFocusKey ?? 'default';
    const payload: CachedChatPlannerResult = {
      savedAt: Date.now(),
      result,
    };
    await AsyncStorage.setItem(v2Key(tripId, mode, fk), JSON.stringify(payload));
  } catch {
    // ignore caching failures
  }
}

/**
 * @param plannerFocusKey - Itinerary only; ignored for packages (always `default`).
 */
export async function getCachedChatPlannerResult(
  tripId: string,
  mode: 'itinerary' | 'packages',
  plannerFocusKey?: string,
): Promise<CachedChatPlannerResult | null> {
  try {
    const fk = mode === 'packages' ? 'default' : plannerFocusKey ?? 'default';
    let parsed = parseCached(await AsyncStorage.getItem(v2Key(tripId, mode, fk)));
    if (parsed) return parsed;
    // One-time read of pre-v2 cache (default focus only).
    if (mode === 'itinerary' && fk === 'default') {
      parsed = parseCached(await AsyncStorage.getItem(legacyKey(tripId, mode)));
      if (parsed) return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

